import React, { useState } from 'react';
import { PlayCircle, FilePlus2, BookOpen, Shuffle, ChevronDown, ChevronUp } from 'lucide-react';
import Modal from './Modal';
import MOCK_CASES from '../data/mockCases';
import { getStreams } from '../utils/caseStreams';
import { toast } from '../utils/toast';

// One case in the picker. Cases with diagnostic streams get a variant list:
// tick the variants to run and they play in sequence, one after another, with
// a clean board between each.
function CaseRow({ caseDef, onStart }) {
  const streams = getStreams(caseDef);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(() => streams.map((s) => s.id));

  const toggle = (id) =>
    setPicked((cur) => (cur.includes(id)
      // Keep the case's own order regardless of click order.
      ? cur.filter((x) => x !== id)
      : streams.map((s) => s.id).filter((x) => cur.includes(x) || x === id)));

  return (
    <div className="border border-line rounded-card p-3.5 hover:border-accent/40 transition">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-body font-semibold text-ink">{caseDef.title}</h3>
          <p className="text-meta text-ink-2 mt-0.5">
            {caseDef.presentingSymptom} · {(caseDef.history || []).length} history ·{' '}
            {(caseDef.physical || []).length} exam · {(caseDef.labs || []).length} results
            {streams.length > 0 && (
              <span className="text-accent font-medium"> · {streams.length} variants</span>
            )}
          </p>
        </div>
        <button
          onClick={() => onStart(caseDef, streams.length ? picked : [])}
          className="flex items-center gap-1.5 bg-accent text-white px-3.5 py-2 rounded-ctl text-meta font-medium hover:bg-accent-strong transition flex-shrink-0"
        >
          <PlayCircle className="w-4 h-4" />Start case
          {picked.length > 1 && <span className="opacity-80">({picked.length} in sequence)</span>}
        </button>
      </div>

      {streams.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-line">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-1.5 text-meta font-medium text-accent hover:text-accent"
            aria-expanded={open}
          >
            <Shuffle className="w-3.5 h-3.5" />
            Diagnostic variants ({picked.length}/{streams.length} queued)
            {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {open && (
            <div className="mt-2 space-y-1.5">
              {streams.map((s, i) => {
                const at = picked.indexOf(s.id);
                return (
                  <div key={s.id} className="flex items-start gap-2 bg-page rounded-ctl px-2.5 py-2">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-accent"
                      checked={at >= 0}
                      onChange={() => toggle(s.id)}
                      aria-label={`Queue variant ${s.label}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-meta font-medium text-ink">
                        {at >= 0 && <span className="text-accent mr-1">{at + 1}.</span>}
                        {s.label || `Variant ${i + 1}`}
                      </p>
                      {s.summary && <p className="text-meta text-ink-2 mt-0.5 leading-snug">{s.summary}</p>}
                    </div>
                    <button
                      onClick={() => onStart(caseDef, [s.id])}
                      className="text-meta text-accent hover:text-accent font-medium flex-shrink-0"
                      title="Run only this variant"
                    >
                      Run this one
                    </button>
                  </div>
                );
              })}
              <p className="text-meta text-ink-2">
                Queued variants run one after another, finish a variant, then “Next variant” restarts the flow
                on a clean board with the same class.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Facilitator picks a case (their own or a built-in) to begin the guided
// flow: History → Physical → Differential → Labs → CDM.
export default function LoadCaseModal({ activeCase, customCases = [], onLoad, onManage, onClose }) {
  const [blankOpen, setBlankOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [symptom, setSymptom] = useState('');
  const [demographics, setDemographics] = useState('');
  const [dx, setDx] = useState('');
  const [dxExplanation, setDxExplanation] = useState('');

  const load = (caseDef, streamIds = []) => {
    onLoad(caseDef, { streamIds });
    toast(`Case loaded: ${caseDef.title}. The class starts with History.`, 'success');
    onClose();
  };

  const loadBlank = () => {
    if (!title.trim() || !symptom.trim()) return;
    load({
      id: 'custom',
      title: title.trim(),
      presentingSymptom: symptom.trim(),
      demographics: demographics.trim(),
      history: [],
      physical: [],
      labs: [],
      diagnosis: dx.trim() ? { text: dx.trim(), explanation: dxExplanation.trim() } : null,
    });
  };

  return (
    <Modal title="Load a case" onClose={onClose} wide>
      {activeCase && (
        <p className="text-meta text-flag-amber bg-flag-amber/5 border border-flag-amber/40 rounded-ctl px-3 py-2 mb-4">
          A case is already running (“{activeCase}”). Loading a new one restarts the flow at History, 
          board entries stay, but reveals and overrides reset.
        </p>
      )}
      {customCases.length > 0 && (
        <>
          <h3 className="text-meta font-semibold text-ink-2 uppercase tracking-wide mb-1.5">My cases</h3>
          <div className="space-y-2.5 mb-4">
            {customCases.map((c) => <CaseRow key={c.id} caseDef={c} onStart={load} />)}
          </div>
          <h3 className="text-meta font-semibold text-ink-2 uppercase tracking-wide mb-1.5">Built-in</h3>
        </>
      )}
      <div className="space-y-2.5">
        {MOCK_CASES.map((c) => <CaseRow key={c.id} caseDef={c} onStart={load} />)}
      </div>

      <div className="mt-4 pt-4 border-t border-line">
        <button
          onClick={() => { onClose(); onManage(); }}
          className="flex items-center gap-1.5 text-meta text-accent hover:text-accent font-medium mb-2"
        >
          <BookOpen className="w-3.5 h-3.5" />Open the case library, view, edit and build cases
        </button>
        {!blankOpen ? (
          <button onClick={() => setBlankOpen(true)} className="flex items-center gap-1.5 text-meta text-ink-2 hover:text-accent transition">
            <FilePlus2 className="w-3.5 h-3.5" />Create a blank case (write your own findings as you go)
          </button>
        ) : (
          <div className="space-y-2">
            <div className="grid sm:grid-cols-2 gap-2">
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Case title *" className="px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25" autoFocus />
              <input value={symptom} onChange={(e) => setSymptom(e.target.value)} placeholder="Presenting symptom *" className="px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25" />
            </div>
            <input value={demographics} onChange={(e) => setDemographics(e.target.value)} placeholder="One-line patient description (optional)" className="w-full px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25" />
            <div className="grid sm:grid-cols-2 gap-2">
              <input value={dx} onChange={(e) => setDx(e.target.value)} placeholder="Final diagnosis (optional, set later otherwise)" className="px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25" />
              <input value={dxExplanation} onChange={(e) => setDxExplanation(e.target.value)} placeholder="Diagnosis explanation (optional)" className="px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25" />
            </div>
            <p className="text-meta text-ink-2">Add findings and labs from the “Answer key” panels on each tab as the case runs.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setBlankOpen(false)} className="px-3 py-1.5 text-meta text-ink-2 hover:bg-accent-tint rounded-ctl transition">Cancel</button>
              <button onClick={loadBlank} disabled={!title.trim() || !symptom.trim()} className="px-4 py-1.5 bg-accent text-white text-meta font-medium rounded-ctl hover:bg-accent-strong transition disabled:opacity-50">
                Start blank case
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
